import type { ChatFileInfo } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { createChatRoom, sendMessageToChat } from "@/common/services/chat"
import { handleError } from "@/common/utils/error-handler"

import type { PlaygroundChatContext } from "../playgroundTypes"
import type { PlaygroundRootState } from "../selectors"

interface SendMessageParams {
  chatId: string
  content: string
  messageId: string
  context?: PlaygroundChatContext
  authToken?: string | null
  knowledgeIds?: string[]
  images?: string[]
  /** File info for optimistic UI - includes name for display */
  files?: ChatFileInfo[]
}

/**
 * Send message thunk - Playground only
 * Handles both new chat creation and existing chat messages
 *
 * Uses chat.isNew flag to determine which endpoint to call:
 * - isNew=true → POST /api/chatrooms (create chatroom + send first message)
 * - isNew=false → POST /api/chatrooms/:id/messages (send to existing chat)
 */
export const sendMessage = createAsyncThunk(
  "playgroundChat/sendMessage",
  async (
    { chatId, content, messageId, context, authToken, knowledgeIds, images }: SendMessageParams,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as PlaygroundRootState
      const chat = state.playgroundChat.chats[chatId]
      const isNewChat = chat?.isNew ?? false

      const shouldGenerateTopic = !chat?.topic
      const resolvedAgentId = context?.agentId ?? state.playgroundChat.selectedAgentId ?? ""

      // Filter out any null/undefined values from arrays
      const validKnowledgeIds = (knowledgeIds || []).filter((id): id is string => Boolean(id))
      const validImages = (images || []).filter((url): url is string => Boolean(url))

      if (isNewChat && context?.chatRoomId) {
        await createChatRoom({
          content,
          id: messageId,
          knowledgeIds: validKnowledgeIds,
          images: validImages.length > 0 ? validImages : undefined,
          agentId: resolvedAgentId,
          chatRoomId: context.chatRoomId,
          authToken,
          generateTopic: shouldGenerateTopic
        })
        // `wasNewChatroom` marks the request that WROTE the `Chat` row. The
        // WebSocket middleware re-joins `chat:<id>` on it, because the join it
        // emitted at `openChat` named a room that did not exist yet and was
        // refused. Reporting the branch actually taken keeps that flag honest.
        return { chatId: context.chatRoomId, messageId, content, wasNewChatroom: true }
      } else if (chatId) {
        await sendMessageToChat({
          chatRoomId: chatId,
          content,
          id: messageId,
          knowledgeIds: validKnowledgeIds,
          images: validImages.length > 0 ? validImages : undefined,
          agentId: resolvedAgentId,
          authToken,
          generateTopic: shouldGenerateTopic
        })
        return { chatId, messageId, content, wasNewChatroom: false }
      } else {
        throw new Error("Invalid chat configuration")
      }
    } catch (error) {
      // Report the real error here; the thunk only forwards a string to Redux,
      // so the downstream UX handler cannot classify the original API error.
      handleError(error, {
        component: "sendMessageThunk",
        operation: "sendMessage",
        silent: true
      })
      return rejectWithValue(error instanceof Error ? error.message : String(error))
    }
  }
)
